import { motion } from 'motion/react';
import { ArrowRight, ArrowUpRight } from 'lucide-react';

type PilgrimCtaProps = {
	eyebrow?: string;
	title?: string;
	description?: string;
	giantLabel?: string;
	primaryCta?: {
		label?: string;
		url?: string;
	};
	secondaryCta?: {
		label?: string;
		url?: string;
	};
};

const fadeUp = (delay = 0) => ({
	hidden: { opacity: 0, y: 20 },
	visible: { opacity: 1, y: 0, transition: { duration: 0.72, delay, ease: [0.16, 1, 0.3, 1] as const } },
});

export function PilgrimCta({ eyebrow, title, description, giantLabel, primaryCta, secondaryCta }: PilgrimCtaProps) {
	return (
		<section className="relative overflow-hidden border-b border-white/[0.06] bg-foreground">
			{/* Warm atmospheric glow — gold bloom */}
			<div
				aria-hidden="true"
				className="pointer-events-none absolute left-[30%] top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent/[0.07] blur-[160px]"
			/>
			{/* Cold counter-glow bottom-right */}
			<div
				aria-hidden="true"
				className="pointer-events-none absolute bottom-0 right-[10%] h-[320px] w-[320px] rounded-full bg-accent-light/[0.04] blur-[120px]"
			/>

			{/* Giant watermark */}
			<motion.p
				initial={{ opacity: 0, y: 50 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true, amount: 0.06 }}
				transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
				aria-hidden="true"
				className="pointer-events-none absolute inset-x-0 bottom-0 select-none translate-y-[20%] overflow-hidden whitespace-nowrap text-center text-[9rem] font-semibold leading-none tracking-[-0.07em] text-white/[0.035] sm:text-[13rem] md:text-[17rem] lg:text-[21rem] xl:text-[26rem]"
			>
				{giantLabel || 'Sahibzade'}
			</motion.p>

			{/* Top hairline */}
			<div
				aria-hidden="true"
				className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"
			/>

			<div className="relative mx-auto max-w-6xl px-6 py-28 md:px-12 lg:px-16 lg:py-36">
				<div className="grid gap-20 lg:grid-cols-[minmax(0,1.15fr)_320px] lg:items-end">

					{/* ── Left: text ─────────────────────────────── */}
					<motion.div
						initial="hidden"
						whileInView="visible"
						viewport={{ once: true, amount: 0.06 }}
						variants={{ visible: { transition: { staggerChildren: 0.07 } } }}
					>
						{/* Eyebrow */}
						<motion.div variants={fadeUp(0)} className="flex items-center gap-3">
							<div className="h-px w-6 shrink-0 bg-accent/60" aria-hidden="true" />
							<p className="text-[10px] font-semibold uppercase tracking-[0.44em] text-accent/70">
								{eyebrow || 'Visit, pray, connect'}
							</p>
						</motion.div>

						{/* Headline */}
						<motion.h2
							variants={fadeUp(0.04)}
							className="mt-7 font-serif text-5xl leading-[0.94] tracking-tight text-white md:text-6xl lg:text-[4.5rem]"
						>
							{title || 'An open invitation to the sangat.'}
						</motion.h2>

						{/* Description */}
						<motion.p
							variants={fadeUp(0.1)}
							className="mt-7 max-w-md text-[14.5px] leading-[1.9] text-white/38"
						>
							{description || 'Visiting information, seva invitations, or a gentle call to reach out — use this section as a closing statement.'}
						</motion.p>
					</motion.div>

					{/* ── Right: CTA column ──────────────────────── */}
					<motion.div
						initial={{ opacity: 0, y: 28 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.06 }}
						transition={{ duration: 0.65, delay: 0.14, ease: [0.16, 1, 0.3, 1] }}
						className="flex flex-col gap-4 self-end"
					>
					 

						{/* Primary CTA */}
						<a
							href={primaryCta?.url || '/contact'}
							className={[
								'group relative inline-flex w-full items-center justify-center gap-2.5 overflow-hidden rounded-[0.7rem]',
								'bg-accent px-6 py-3.5 text-[13px] font-semibold tracking-[0.01em] text-accent-foreground',
								'shadow-[0_1px_0_rgba(255,255,255,0.13)_inset,0_2px_16px_rgba(184,154,50,0.40)]',
								'transition-all duration-300 hover:brightness-105 hover:shadow-[0_1px_0_rgba(255,255,255,0.13)_inset,0_4px_28px_rgba(184,154,50,0.55)]',
							].join(' ')}
						>
							{/* inset top edge highlight */}
							<span
								aria-hidden="true"
								className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent"
							/>
							{primaryCta?.label || 'Plan your visit'}
							<ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
						</a>

						{/* Secondary CTA — text link style */}
						<a
							href={secondaryCta?.url || '/programs'}
							className="group inline-flex w-full items-center justify-center gap-1.5 py-2 text-[13px] font-medium text-white  transition-colors duration-200 hover:text-white/70"
						>
							{secondaryCta?.label || 'Explore programs'}
							<ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
						</a>

						{/* Bottom divider */}
						<div className="h-px bg-white/[0.06]" aria-hidden="true" />
					</motion.div>
				</div>
			</div>
		</section>
	);
}
